/**
 * Sign Up Page
 *
 * User registration page with role selection.
 */

import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth/AuthContext'

export default function SignUpPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    phone: '',
    role: 'buyer',
    company_name: '',
    buyer_category: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const { signUp, user, profile } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (user && profile) {
      const dashboardPath = profile.role === 'buyer'
        ? '/dashboard/buyer'
        : profile.role === 'developer'
        ? '/dashboard/developer'
        : profile.role === 'admin'
        ? '/dashboard/admin'
        : '/'

      navigate(dashboardPath, { replace: true })
    }
  }, [user, profile, navigate])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    if (formData.role === 'developer' && !formData.company_name) {
      setError('Company name is required for developers')
      return
    }

    setLoading(true)

    const metadata = {
      full_name: formData.full_name,
      phone: formData.phone || null,
      role: formData.role,
    }

    if (formData.role === 'developer') {
      metadata.company_name = formData.company_name
    }

    if (formData.role === 'buyer' && formData.buyer_category) {
      metadata.buyer_category = formData.buyer_category
    }

    const { error: signUpError } = await signUp(
      formData.email,
      formData.password,
      metadata
    )

    if (signUpError) {
      setError(signUpError.message)
      setLoading(false)
    } else {
      // Show success message
      navigate('/signin', {
        state: { message: 'Account created successfully! Please sign in.' }
      })
    }
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-secondary-50 py-12 px-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">
            Create Your Account
          </h1>
          <p className="text-secondary-600">
            Join Beaver to discover off-plan properties
          </p>
        </div>

        {/* Form Card */}
        <div className="card p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Alert */}
            {error && (
              <div className="alert-error">
                <p>{error}</p>
              </div>
            )}

            {/* Role Selection */}
            <div className="form-group">
              <label className="label">I am a</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, role: 'buyer' }))}
                  className={`p-4 rounded-lg border-2 text-sm font-medium transition-colors ${
                    formData.role === 'buyer'
                      ? 'border-primary-600 bg-primary-50 text-primary-700'
                      : 'border-secondary-200 bg-white text-secondary-700 hover:border-secondary-300'
                  }`}
                  disabled={loading}
                >
                  Homebuyer
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, role: 'developer' }))}
                  className={`p-4 rounded-lg border-2 text-sm font-medium transition-colors ${
                    formData.role === 'developer'
                      ? 'border-primary-600 bg-primary-50 text-primary-700'
                      : 'border-secondary-200 bg-white text-secondary-700 hover:border-secondary-300'
                  }`}
                  disabled={loading}
                >
                  Developer
                </button>
              </div>
            </div>

            {/* Buyer Category (Buyer only) */}
            {formData.role === 'buyer' && (
              <div className="form-group">
                <label className="label">Are you a first home buyer?</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, buyer_category: 'first_home' }))}
                    className={`p-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                      formData.buyer_category === 'first_home'
                        ? 'border-green-600 bg-green-50 text-green-700'
                        : 'border-secondary-200 bg-white text-secondary-700 hover:border-secondary-300'
                    }`}
                    disabled={loading}
                  >
                    <span className="block font-semibold">Yes</span>
                    <span className="block text-xs mt-0.5 opacity-75">First home buyer</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, buyer_category: 'other' }))}
                    className={`p-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                      formData.buyer_category === 'other'
                        ? 'border-primary-600 bg-primary-50 text-primary-700'
                        : 'border-secondary-200 bg-white text-secondary-700 hover:border-secondary-300'
                    }`}
                    disabled={loading}
                  >
                    <span className="block font-semibold">No</span>
                    <span className="block text-xs mt-0.5 opacity-75">Investor or upgrading</span>
                  </button>
                </div>
                {formData.buyer_category === 'first_home' && (
                  <p className="form-help text-green-700 mt-2">
                    Great! As a first home buyer, you may be eligible for up to $10,000 towards your deposit.
                  </p>
                )}
              </div>
            )}

            {/* Full Name */}
            <div className="form-group">
              <label htmlFor="full_name" className="label">
                Full Name
              </label>
              <input
                id="full_name"
                name="full_name"
                type="text"
                value={formData.full_name}
                onChange={handleChange}
                className="input"
                placeholder="John Doe"
                required
                disabled={loading}
              />
            </div>

            {/* Company Name (Developer only) */}
            {formData.role === 'developer' && (
              <div className="form-group">
                <label htmlFor="company_name" className="label">
                  Company Name
                </label>
                <input
                  id="company_name"
                  name="company_name"
                  type="text"
                  value={formData.company_name}
                  onChange={handleChange}
                  className="input"
                  placeholder="ABC Developments Ltd"
                  required
                  disabled={loading}
                />
              </div>
            )}

            {/* Email */}
            <div className="form-group">
              <label htmlFor="email" className="label">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className="input"
                placeholder="you@example.com"
                required
                disabled={loading}
              />
            </div>

            {/* Phone */}
            <div className="form-group">
              <label htmlFor="phone" className="label">
                Phone Number (Optional)
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className="input"
                placeholder="+64 21 123 4567"
                disabled={loading}
              />
            </div>

            {/* Password */}
            <div className="form-group">
              <label htmlFor="password" className="label">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                className="input"
                placeholder="Minimum 8 characters"
                required
                disabled={loading}
                minLength={8}
              />
              <p className="form-help">Must be at least 8 characters</p>
            </div>

            {/* Confirm Password */}
            <div className="form-group">
              <label htmlFor="confirmPassword" className="label">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="input"
                placeholder="Re-enter your password"
                required
                disabled={loading}
                minLength={8}
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <span className="spinner mr-2"></span>
                  Creating account...
                </span>
              ) : (
                'Create Account'
              )}
            </button>
          </form>
        </div>

        {/* Sign In Link */}
        <p className="text-center mt-6 text-secondary-600">
          Already have an account?{' '}
          <Link to="/signin" className="text-primary-600 hover:text-primary-700 font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
